import { CheckIcon } from '@heroicons/react/20/solid';
import { MagnifyingGlassIcon, StopIcon } from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  StopIcon as StopIconSolid,
} from '@heroicons/react/24/solid';
import { ChevronDownIcon, Minus } from 'lucide-react';
import React, { ReactNode, useMemo, useState } from 'react';
import { Link, RelativeRoutingType, To } from 'react-router';

import { Input } from '@/components/form/Input';
import { OverflowText } from '@/components/OverflowText';
import { Tooltip } from '@/components/Tooltip';
import {
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import type { Severity } from '@/types/dropdown.types';
import { QueryStatus } from '@/utils/api';
import { cn } from '@/utils/classname';
import { capitalize } from '@/utils/lodash.util';
import { useStorage } from '@/utils/storage/useStorage.util';

export type MenuProps = {
  items: Omit<MenuItemProps, 'isFocused'>[];
  onClick?: (value?: string) => void;
  value?: string | string[];
  className?: string;
  multiSelect?: boolean;
  onSelect?: (value: string[]) => void;
  emptyState?: {
    label: ReactNode;
    hide?: boolean;
  };
  status?: QueryStatus;
  search?: {
    value: string;
    onChange: (value: string) => void;
    onKeyDown?: (e: React.KeyboardEvent<Element>) => void;
    name: string;
    className?: string;
    startIconClassName?: string;
  };
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
  /**
   * Width forwarded from the Dropdown wrapper. Applied on DropdownMenuContent;
   * accepted here so it is not spread onto the menu body element.
   */
  width?: number;
};

interface subMenuOpenProps {
  expandedGroups?: Set<string>;
  setExpandedGroups?: (expandedGroups: Set<string>) => void;
  selectedItems?: string[];
  setSelectedItems?: (
    selected: string[] | ((prev: string[]) => string[])
  ) => void;
}

export function Menu(props: MenuProps) {
  const {
    className,
    items: unparsedItems,
    onClick,
    multiSelect,
    emptyState,
    search,
    status,
    onSelectAll,
    onDeselectAll,
  } = props;

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useStorage<string[]>(
    {
      parentState:
        typeof props.value === 'string' ? [props.value] : props.value,
      onParentStateChange: props.onSelect,
    },
    []
  );

  const items = useMemo(() => {
    return unparsedItems.filter(item => !item.hide);
  }, [unparsedItems]);

  // Calculate tri-state for Select All checkbox
  const selectAllState = useMemo(() => {
    if (!multiSelect || (!onSelectAll && !onDeselectAll)) return null;

    const selectableItems = items.filter(
      item => !item.disabled && !item.isMenuGroup && item.value !== undefined
    );
    const selectedCount = selectableItems.filter(item =>
      selected.includes(item.value!)
    ).length;

    if (selectedCount === 0) return { checked: false, indeterminate: false };
    if (selectedCount === selectableItems.length)
      return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  }, [items, selected, multiSelect, onSelectAll, onDeselectAll]);

  return (
    <div
      className={cn(
        'bg-chariot-background-primary ring-chariot-stroke relative w-full overflow-auto rounded-[2px] shadow-lg ring-1 outline-hidden',
        className
      )}
    >
      {search && (
        <div
          data-dropdown-search
          ref={node => {
            // Focus the search input on open (replaces Floating-UI initialFocus).
            // DropdownMenu has no onOpenAutoFocus hook, so we focus here on mount.
            node?.querySelector<HTMLInputElement>('input')?.focus();
          }}
          className={cn(
            'bg-chariot-background-primary sticky top-0 z-20',
            search.className
          )}
        >
          <div className="m-0 p-3">
            <Input
              startIcon={
                <MagnifyingGlassIcon
                  className={cn(
                    'text-chariot-text-primary size-4',
                    search.startIconClassName
                  )}
                />
              }
              name={`${search.name.toLowerCase()}Search`}
              placeholder={`Search ${capitalize(search.name)}`}
              className="bg-chariot-background-secondary text-chariot-text-primary placeholder:text-chariot-text-secondary w-full rounded-md border-0 ring-0 focus:outline-hidden focus-visible:[&_input]:outline-hidden!"
              value={search.value}
              onChange={e => {
                e.preventDefault();
                e.stopPropagation();
                search.onChange(e.target.value);
              }}
              onKeyDown={e => {
                // Prevent Radix typeahead from hijacking search keystrokes.
                e.stopPropagation();
                search.onKeyDown?.(e);
              }}
            />
          </div>
        </div>
      )}
      {items.length === 0 && status !== 'pending' && !emptyState?.hide && (
        <div
          className={cn(
            'text-chariot-text-secondary flex items-center text-xs font-medium',
            menuMarginClassName,
            className
          )}
        >
          {emptyState?.label || 'No items found'}
        </div>
      )}
      {status === 'pending' &&
        [...Array(5).keys()].map(index => (
          <div key={index} className="mb-2 h-8 w-full px-4">
            <Skeleton shape="block" className="rounded-none" />
          </div>
        ))}
      {selectAllState && (
        <>
          <DropdownMenuCheckboxItem
            checked={
              selectAllState.indeterminate
                ? 'indeterminate'
                : selectAllState.checked
            }
            className="px-0 pl-0 [&>span:first-child]:hidden"
            onSelect={event => {
              event.preventDefault();
              if (selectAllState.checked || selectAllState.indeterminate) {
                onDeselectAll?.();
              } else {
                onSelectAll?.();
              }
            }}
          >
            <div
              className={cn(
                'hover:bg-chariot-background-secondary relative flex w-full cursor-pointer items-center rounded-[2px] py-2',
                menuMarginClassName
              )}
            >
              <div className="flex items-center gap-2">
                <CheckboxIcon
                  isChecked={selectAllState.checked}
                  isIndeterminate={selectAllState.indeterminate}
                />
                <span className="text-chariot-text-primary text-sm font-semibold">
                  Select All
                </span>
              </div>
            </div>
          </DropdownMenuCheckboxItem>
          <DropdownMenuSeparator />
        </>
      )}
      {items.map((item, index) => (
        <MenuItem
          key={index}
          {...item}
          multiSelect={multiSelect}
          expandedGroups={expandedGroups}
          setExpandedGroups={setExpandedGroups}
          selectedItems={selected} // Pass selected array to child components
          setSelectedItems={setSelected} // Pass setSelected function to child components
          isSelected={
            item.value === undefined ? false : selected.includes(item.value)
          }
          onClick={(...args) => {
            onClick?.(...args);
            item.onClick?.(...args);

            if (item.value !== undefined) {
              if (multiSelect) {
                setSelected(prev => {
                  let newSelected = prev;
                  if (multiSelect && item.value !== undefined) {
                    if (item.value) {
                      // Toggle the value
                      if (prev.includes(item.value)) {
                        newSelected = prev.filter(v => v !== item.value);
                      } else {
                        newSelected = [...prev, item.value];
                      }
                      // Toggle "All option" and other values
                      newSelected =
                        newSelected.length > 0
                          ? newSelected.filter(v => v !== '')
                          : [];
                    } else {
                      // If 'All' option is selected, remove all other values
                      newSelected = [];
                    }
                  }

                  return newSelected;
                });
              } else {
                setSelected([item.value]);
              }
            }
          }}
        />
      ))}
    </div>
  );
}

const menuMarginClassName = `m-[8px] w-[calc(100%-16px)]`;

export interface MenuItemProps {
  label: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  labelSuffix?: string | number | React.ReactElement<any>;
  value?: string;
  icon?: React.ReactNode;
  description?: string;
  type?: 'label' | 'divider' | 'group';
  isDivider?: boolean;
  to?: To;
  relative?: RelativeRoutingType;
  download?: boolean;
  newTab?: boolean;
  disabled?: boolean;
  hide?: boolean;
  count?: string | React.ReactNode;
  onClick?: (value?: string) => void;
  isFocused?: boolean;
  isSelected?: boolean;
  styleType?: string;
  className?: string;
  classNameSelected?: string;
  tooltip?: string;
  isLoading?: boolean;
  submenu?: MenuItemProps[];
  // Group properties - these are additional optional properties for group functionality
  // We use a different property name to avoid conflicts with external DropdownOption types
  isMenuGroup?: boolean;
  menuGroupItems?: MenuItemProps[];
  isMenuGroupExpanded?: boolean;
  severity?: Severity;
  /**
   * Renders the item label and icon in the destructive color. Opt-in — existing
   * items are unaffected. Disabled state still wins (a disabled destructive item
   * renders in the disabled color, not red).
   */
  destructive?: boolean;
}

function MenuItem(
  props: MenuItemProps & subMenuOpenProps & { multiSelect?: boolean }
) {
  if (props.hide) {
    return null;
  }

  if (props.type === 'label') {
    const { label, className } = props;

    return (
      <DropdownMenuLabel
        className={cn(
          'bg-chariot-background-primary text-chariot-text-secondary sticky top-0 z-10 mx-2 px-3 pt-5 pb-2 text-xs font-medium',
          className
        )}
      >
        {label}
      </DropdownMenuLabel>
    );
  }

  if (props.type === 'divider' || props.isDivider) {
    return (
      <DropdownMenuSeparator
        className={cn(
          'border-chariot-stroke m-2 -ml-2 border-t',
          props.className
        )}
      />
    );
  }

  // Handle group type items
  if (props.type === 'group' || props.isMenuGroup) {
    const {
      label,
      value = '',
      menuGroupItems = [],
      expandedGroups,
      setExpandedGroups,
      multiSelect,
      className,
      selectedItems,
      setSelectedItems,
    } = props;

    const groupId = value || String(label);
    const isExpanded = expandedGroups?.has(groupId) || false;

    const toggleGroup = () => {
      if (expandedGroups && setExpandedGroups) {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(groupId)) {
          newExpanded.delete(groupId);
        } else {
          newExpanded.add(groupId);
        }
        setExpandedGroups(newExpanded);
      }
    };

    // Calculate tri-state for group checkbox
    const selectableGroupItems = menuGroupItems.filter(
      item => !item.disabled && item.value !== undefined
    );
    const selectedGroupCount = selectableGroupItems.filter(item =>
      selectedItems?.includes(item.value!)
    ).length;

    const isGroupChecked = selectedGroupCount === selectableGroupItems.length;
    const isGroupIndeterminate =
      selectedGroupCount > 0 &&
      selectedGroupCount < selectableGroupItems.length;

    const handleGroupCheckbox = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!setSelectedItems) return;

      if (isGroupChecked || isGroupIndeterminate) {
        // Deselect all group items
        setSelectedItems(prev =>
          prev.filter(
            val => !selectableGroupItems.some(item => item.value === val)
          )
        );
      } else {
        // Select all group items
        setSelectedItems(prev => [
          ...prev,
          ...selectableGroupItems
            .map(item => item.value!)
            .filter(val => !prev.includes(val)),
        ]);
      }
    };

    return (
      <>
        {/* Non-closing group header row (preventDefault keeps the menu open) */}
        <DropdownMenuItem
          className="p-0 focus:bg-transparent"
          onSelect={event => event.preventDefault()}
        >
          <div
            className={cn(
              'hover:bg-chariot-background-secondary relative flex w-full items-center justify-between rounded-[2px] py-2 text-start',
              menuMarginClassName,
              className
            )}
          >
            <div className="flex items-center gap-2">
              {multiSelect && (
                <div onClick={handleGroupCheckbox}>
                  <CheckboxIcon
                    isChecked={isGroupChecked}
                    isIndeterminate={isGroupIndeterminate}
                  />
                </div>
              )}
              <button
                onClick={toggleGroup}
                className="flex flex-1 items-center justify-between"
              >
                <span className="text-chariot-text-primary pl-4 text-sm font-medium">
                  {label}
                </span>
                <ChevronDownIcon
                  className={cn(
                    'text-chariot-text-secondary size-4 transition-transform',
                    isExpanded ? 'rotate-180' : ''
                  )}
                />
              </button>
            </div>
          </div>
        </DropdownMenuItem>
        {isExpanded &&
          menuGroupItems.map((item, index) => (
            <div key={`${groupId}-${index}`} className="ml-4">
              <MenuItem
                {...item}
                multiSelect={multiSelect}
                expandedGroups={expandedGroups}
                setExpandedGroups={setExpandedGroups}
                selectedItems={props.selectedItems}
                setSelectedItems={props.setSelectedItems}
                isSelected={
                  item.value === undefined
                    ? false
                    : props.selectedItems?.includes(item.value) || false
                }
                onClick={(...args) => {
                  props.onClick?.(...args);
                  item.onClick?.(...args);

                  if (item.value !== undefined && props.setSelectedItems) {
                    if (multiSelect) {
                      props.setSelectedItems(prev => {
                        let newSelected = prev || [];
                        if (multiSelect && item.value !== undefined) {
                          if (item.value) {
                            // Toggle the value
                            if (newSelected.includes(item.value)) {
                              newSelected = newSelected.filter(
                                v => v !== item.value
                              );
                            } else {
                              newSelected = [...newSelected, item.value];
                            }
                            // Toggle "All option" and other values
                            newSelected =
                              newSelected.length > 0
                                ? newSelected.filter(v => v !== '')
                                : [];
                          } else {
                            // If 'All' option is selected, remove all other values
                            newSelected = [''];
                          }
                        }
                        return newSelected;
                      });
                    } else {
                      props.setSelectedItems([item.value]);
                    }
                  }
                }}
              />
            </div>
          ))}
      </>
    );
  }

  // Submenu: render as a real Radix Sub (breaks the old Dropdown cycle).
  if (props.submenu && props.submenu.length > 0) {
    const {
      tooltip,
      submenu,
      multiSelect,
      expandedGroups,
      setExpandedGroups,
      selectedItems,
      setSelectedItems,
    } = props;

    return (
      <DropdownMenuSub>
        <Tooltip title={tooltip} placement="right">
          {/* aria-haspopup="dialog" is MANDATORY: Radix SubTrigger defaults to
              "menu"; consumer tests query [aria-haspopup="dialog"]. */}
          <DropdownMenuSubTrigger
            aria-haspopup="dialog"
            disabled={props.disabled}
            className={cn(submenuTriggerClassName(props))}
          >
            <Content {...props} severity={props.severity} />
          </DropdownMenuSubTrigger>
        </Tooltip>
        <DropdownMenuSubContent>
          {submenu.map((subItem, index) => (
            <MenuItem
              key={index}
              {...subItem}
              multiSelect={multiSelect}
              expandedGroups={expandedGroups}
              setExpandedGroups={setExpandedGroups}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              isSelected={
                subItem.value === undefined
                  ? false
                  : selectedItems?.includes(subItem.value) || false
              }
              onClick={(...args) => {
                props.onClick?.(...args);
                subItem.onClick?.(...args);
              }}
            />
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  const { to, relative, newTab, download, disabled, onClick, value } = props;

  // Router-Link item: keep target/download/middle-click semantics.
  if (to) {
    const isNewTab =
      newTab || download || (typeof to === 'string' && to.startsWith('http'));

    return (
      <DropdownMenuItem
        asChild
        disabled={disabled}
        className={cn('p-0 focus:bg-transparent')}
        onSelect={() => {
          if (!disabled) {
            onClick?.(value);
          }
        }}
      >
        <Link
          to={to}
          relative={relative}
          {...(isNewTab
            ? {
                target: '_blank',
                rel: 'noreferrer',
              }
            : {})}
          {...(download
            ? {
                download: true,
              }
            : {})}
        >
          <MenuItemRow {...props}>
            <Content {...props} />
          </MenuItemRow>
        </Link>
      </DropdownMenuItem>
    );
  }

  // Multi-select item: stay open on select (preventDefault).
  if (props.multiSelect) {
    return (
      <Tooltip title={props.tooltip} placement="right">
        <DropdownMenuCheckboxItem
          checked={Boolean(props.isSelected)}
          disabled={disabled || props.isLoading}
          className={cn(
            'px-0 [&>span:first-child]:hidden',
            menuItemRowClassName(props)
          )}
          onSelect={event => {
            event.preventDefault();
            if (!disabled) {
              onClick?.(value);
            }
          }}
        >
          <Content {...props} severity={props.severity} />
        </DropdownMenuCheckboxItem>
      </Tooltip>
    );
  }

  // Plain single-select item: Radix auto-closes on select.
  return (
    <Tooltip title={props.tooltip} placement="right">
      <DropdownMenuItem
        disabled={disabled || props.isLoading}
        className={cn(menuItemRowClassName(props))}
        onSelect={() => {
          if (!disabled) {
            onClick?.(value);
          }
        }}
      >
        <Content {...props} severity={props.severity} />
      </DropdownMenuItem>
    </Tooltip>
  );
}

function menuItemRowClassName(props: MenuItemProps): string {
  const { isFocused, isSelected, classNameSelected, disabled, className } =
    props;
  return cn(
    'relative text-start rounded-[2px] flex items-center justify-start m-0 py-2 hover:bg-chariot-background-secondary',
    isFocused ? 'outline-hidden z-10' : '',
    isSelected ? 'bg-chariot-background-secondary group' : '',
    isSelected && classNameSelected ? classNameSelected : '',
    menuMarginClassName,
    disabled && 'disabled:bg-chariot-background-primary',
    className
  );
}

function submenuTriggerClassName(props: MenuItemProps): string {
  return menuItemRowClassName(props);
}

// Lightweight row wrapper for Link items (Radix Item provides the focusable
// element via asChild, the inner Link is the anchor).
function MenuItemRow(props: MenuItemProps & { children: ReactNode }) {
  return (
    <div className={cn(menuItemRowClassName(props), 'w-full')}>
      {props.children}
    </div>
  );
}

function Content(
  props: MenuItemProps & { multiSelect?: boolean; severity?: Severity }
) {
  const {
    label,
    labelSuffix,
    icon,
    description,
    disabled: controlledDisabled,
    count,
    isSelected,
    isLoading,
    multiSelect,
    severity,
    destructive,
  } = props;
  const labelText = !description && !count;

  const disabled = isLoading ? false : controlledDisabled;

  return (
    <div className="flex w-full items-center gap-2">
      {multiSelect && <CheckboxIcon isChecked={Boolean(isSelected)} />}
      {severity && (
        <div
          className={cn(
            'size-4 shrink-0 rounded-xs border',
            `bg-chariot-status-${severity}`,
            `border-chariot-stroke-${severity}`
          )}
        />
      )}
      {icon && (
        <div
          className={cn(
            `[&>svg]:text-chariot-text-primary [&>svg]:size-5`,
            disabled && '[&>svg]:text-chariot-text-disabled',
            destructive && !disabled && '[&>svg]:text-destructive'
          )}
        >
          {icon}
        </div>
      )}
      <div className="flex w-full flex-col overflow-hidden">
        {/* Row 1: label + inline count/shortcut */}
        <div className="flex w-full items-center justify-between overflow-hidden">
          <div className="flex min-w-0 flex-1 items-center justify-between text-sm">
            <div
              className={cn(
                'flex w-full justify-between gap-8 overflow-hidden text-ellipsis',
                labelText ? '' : 'font-semibold',
                'leading-8',
                disabled
                  ? 'text-chariot-text-disabled'
                  : 'text-chariot-text-primary',
                destructive && !disabled && 'text-destructive',
                label === 'View All' && 'm-auto'
              )}
            >
              {typeof label === 'string' &&
                (isSelected && !multiSelect ? (
                  <div className="flex items-center gap-2">
                    <OverflowText text={label} placement="left" />
                    <CheckCircleIcon className="text-chariot-brand-primary size-4" />
                  </div>
                ) : (
                  <OverflowText text={label} placement="left" />
                ))}
              {typeof label !== 'string' && label}
              {labelSuffix}
            </div>
          </div>
          {count != null && count !== '' && (
            <span
              className={cn(
                'pl-4 text-xs text-nowrap',
                disabled
                  ? 'text-chariot-text-disabled'
                  : 'text-chariot-text-primary'
              )}
            >
              {count}
            </span>
          )}
        </div>
        {/* Row 2: description stacked under the label. Stays readable
            (text-secondary, ~13:1) when the item is disabled so gating reasons
            (e.g. "Already approved") remain legible while the label dims. */}
        {description && (
          <div
            className={cn(
              'text-xs leading-tight',
              disabled
                ? 'text-chariot-text-secondary'
                : 'text-chariot-text-primary'
            )}
          >
            {description}
          </div>
        )}
      </div>
    </div>
  );
}

const CheckboxIcon = ({
  isChecked,
  isIndeterminate,
}: {
  isChecked: boolean;
  isIndeterminate?: boolean;
}) => {
  const className = 'box-border size-6 rounded-[2px] text-chariot-text-primary';

  if (isIndeterminate) {
    return (
      <div className="relative">
        <StopIconSolid className={className} />
        <Minus className="text-chariot-background-primary absolute top-1.5 left-1.5 size-3" />
      </div>
    );
  }

  return isChecked ? (
    <div className="relative">
      <StopIconSolid className={className} />
      <CheckIcon className="text-chariot-background-primary absolute top-1.5 left-1.5 size-3" />
    </div>
  ) : (
    <StopIcon className={className} />
  );
};
